import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, setHours, setMinutes } from 'date-fns';
import { CalendarIcon, ChatBubbleLeftIcon, XMarkIcon, ClockIcon, TrophyIcon, MapPinIcon, DocumentTextIcon, CalculatorIcon, CheckCircleIcon, ListBulletIcon, Squares2X2Icon, Bars3Icon } from '@heroicons/react/24/outline';
import 'react-day-picker/dist/style.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DurationDialPicker from './DurationDialPicker';
import { MetadataFieldDefinition } from '../generated-api';
import { getAllMetadataFields } from '../constants/metadataFields';

interface PlayMetadataSectionProps {
  playDate: string;
  comment: string;
  durationMinutes?: number | null;
  onPlayDateChange: (date: string) => void;
  onCommentChange: (comment: string) => void;
  onDurationChange?: (minutes: number | null) => void;
  teams?: string[];
  teamMetadata?: Record<string, Record<string, any>>;
  onTeamMetadataChange?: (metadata: Record<string, Record<string, any>>) => void;
  onMetadataSave?: (metadata: Record<string, Record<string, any>>) => void;
  readOnly?: boolean;
  hideDateButton?: boolean;
  playerMetadataFields?: MetadataFieldDefinition[];
  metadataOptions?: Record<string, string[]>;
}

// Color palette for dynamic fields (avoiding colors used by hardcoded buttons)
const DYNAMIC_FIELD_COLORS = [
  { name: 'rose', border: 'border-rose-400', bg: 'bg-rose-50', bgHover: 'hover:bg-rose-100', text: 'text-rose-600', textLight: 'text-rose-500', ring: 'focus:ring-rose-500', btnBg: 'bg-rose-600', btnHover: 'hover:bg-rose-700' },
  { name: 'orange', border: 'border-orange-400', bg: 'bg-orange-50', bgHover: 'hover:bg-orange-100', text: 'text-orange-600', textLight: 'text-orange-500', ring: 'focus:ring-orange-500', btnBg: 'bg-orange-600', btnHover: 'hover:bg-orange-700' },
  { name: 'cyan', border: 'border-cyan-400', bg: 'bg-cyan-50', bgHover: 'hover:bg-cyan-100', text: 'text-cyan-600', textLight: 'text-cyan-500', ring: 'focus:ring-cyan-500', btnBg: 'bg-cyan-600', btnHover: 'hover:bg-cyan-700' },
  { name: 'lime', border: 'border-lime-400', bg: 'bg-lime-50', bgHover: 'hover:bg-lime-100', text: 'text-lime-600', textLight: 'text-lime-500', ring: 'focus:ring-lime-500', btnBg: 'bg-lime-600', btnHover: 'hover:bg-lime-700' },
  { name: 'fuchsia', border: 'border-fuchsia-400', bg: 'bg-fuchsia-50', bgHover: 'hover:bg-fuchsia-100', text: 'text-fuchsia-600', textLight: 'text-fuchsia-500', ring: 'focus:ring-fuchsia-500', btnBg: 'bg-fuchsia-600', btnHover: 'hover:bg-fuchsia-700' },
];

// Get icon component based on field type
const getFieldIcon = (type: MetadataFieldDefinition['type']) => {
  switch (type) {
    case 'number':
      return CalculatorIcon;
    case 'boolean':
      return CheckCircleIcon;
    case 'select':
      return ListBulletIcon;
    case 'multiselect':
      return Squares2X2Icon;
    case 'order':
      return Bars3Icon;
    case 'text':
    default:
      return DocumentTextIcon;
  }
};

// Get icon component from field icon property or fall back to type-based icon
const getIconComponent = (iconName: string | null | undefined, fieldType: MetadataFieldDefinition['type']): React.ComponentType<{ className?: string }> => {
  // If icon is specified, use it; otherwise fall back to type-based icon
  if (iconName) {
    const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
      'trophy': TrophyIcon,
      'mapPin': MapPinIcon,
      'documentText': DocumentTextIcon,
      'calculator': CalculatorIcon,
      'checkCircle': CheckCircleIcon,
      'listBullet': ListBulletIcon,
      'squares2X2': Squares2X2Icon,
      'bars3': Bars3Icon,
      'clock': ClockIcon,
    };
    return iconMap[iconName] || getFieldIcon(fieldType);
  }
  return getFieldIcon(fieldType);
};

// Get color by index (cycling through available colors)
const getDynamicFieldColor = (index: number) => DYNAMIC_FIELD_COLORS[index % DYNAMIC_FIELD_COLORS.length];

// Sortable team item component for order fields
interface SortableTeamItemProps {
  teamName: string;
  position: number;
  color: typeof DYNAMIC_FIELD_COLORS[number];
}

const SortableTeamItem: React.FC<SortableTeamItemProps> = ({ teamName, position, color }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: teamName });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 border border-edge-input rounded-md bg-surface ${
        isDragging ? 'shadow-lg' : 'shadow-sm'
      }`}
    >
      <div className="flex items-center space-x-3 flex-1" {...attributes} {...listeners}>
        <div className="cursor-move">
          <Bars3Icon className={`w-5 h-5 ${color.text}`} />
        </div>
        <div className="flex-shrink-0">
          <span className={`text-sm font-bold ${color.text}`}>#{position}</span>
        </div>
        <div className="flex-1">
          <span className="text-sm font-medium text-heading">{teamName}</span>
        </div>
      </div>
    </div>
  );
};

// Order field expanded component
interface OrderFieldExpandedProps {
  field: MetadataFieldDefinition;
  index: number;
  color: typeof DYNAMIC_FIELD_COLORS[number];
  Icon: React.ComponentType<{ className?: string }>;
  teams: string[];
  teamMetadata: Record<string, Record<string, any>>;
  onTeamMetadataChange?: (metadata: Record<string, Record<string, any>>) => void;
  onMetadataSave?: (metadata: Record<string, Record<string, any>>) => void;
  onCollapse: () => void;
}

const OrderFieldExpanded: React.FC<OrderFieldExpandedProps> = ({
  field,
  index,
  color,
  Icon,
  teams,
  teamMetadata,
  onTeamMetadataChange,
  onMetadataSave,
  onCollapse,
}) => {
  // Track the latest metadata to ensure we save the most recent state
  const latestMetadataRef = useRef<Record<string, Record<string, any>>>(teamMetadata);
  // Track if positions have been explicitly cleared
  const isClearedRef = useRef<boolean>(false);
  
  // Update ref when prop changes
  useEffect(() => {
    latestMetadataRef.current = teamMetadata;
    // Reset cleared flag if metadata has positions
    const hasPositions = teams.some(teamName => 
      teamMetadata[teamName]?.[field.name] !== undefined &&
      teamMetadata[teamName]?.[field.name] !== null
    );
    if (hasPositions) {
      isClearedRef.current = false;
    }
  }, [teamMetadata, teams, field.name]);

  // Get current order values and create ordered list of teams
  const getOrderedTeams = useCallback(() => {
    // Create array of { teamName, position } from metadata
    const teamsWithPositions = teams.map(teamName => {
      const position = teamMetadata?.[teamName]?.[field.name];
      return {
        teamName,
        position: position !== undefined && position !== null ? Number(position) : null,
      };
    });

    // Separate teams with positions and without
    const withPositions = teamsWithPositions
      .filter(t => t.position !== null)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    const withoutPositions = teamsWithPositions
      .filter(t => t.position === null)
      .map(t => t.teamName);

    // Return ordered teams (with positions first, then unordered ones)
    return [
      ...withPositions.map(t => t.teamName),
      ...withoutPositions
    ];
  }, [teams, teamMetadata, field.name]);

  const [orderedTeams, setOrderedTeams] = useState<string[]>(() => {
    // Initialize on first render
    const teamsWithPositions = teams.map(teamName => {
      const position = teamMetadata?.[teamName]?.[field.name];
      return {
        teamName,
        position: position !== undefined && position !== null ? Number(position) : null,
      };
    });

    const withPositions = teamsWithPositions
      .filter(t => t.position !== null)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    const withoutPositions = teamsWithPositions
      .filter(t => t.position === null)
      .map(t => t.teamName);

    return [
      ...withPositions.map(t => t.teamName),
      ...withoutPositions
    ];
  });

  // Track if user has manually reordered (to prevent auto-reset)
  const hasManualOrderRef = useRef<boolean>(false);

  // Update ordered teams when teams or metadata changes, but only if user hasn't manually reordered
  useEffect(() => {
    if (!hasManualOrderRef.current) {
      setOrderedTeams(getOrderedTeams());
    }
  }, [getOrderedTeams]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = orderedTeams.indexOf(active.id as string);
    const newIndex = orderedTeams.indexOf(over.id as string);

    const newOrderedTeams = arrayMove(orderedTeams, oldIndex, newIndex);
    setOrderedTeams(newOrderedTeams);
    
    // Mark that user has manually reordered
    hasManualOrderRef.current = true;

    // Don't update metadata here - just update the visual order
    // The actual save will happen when "Done" is clicked
    // This ensures we always save the current visual state
  };

  const handleClear = () => {
    const updated = { ...latestMetadataRef.current };
    teams.forEach(teamName => {
      if (updated[teamName]) {
        delete updated[teamName][field.name];
        if (Object.keys(updated[teamName]).length === 0) {
          delete updated[teamName];
        }
      }
    });
    // Update ref and notify parent
    latestMetadataRef.current = updated;
    isClearedRef.current = true;
    hasManualOrderRef.current = false; // Reset manual order flag
    onTeamMetadataChange?.(updated);
    // Reset to default order
    setOrderedTeams([...teams]);
  };

  return (
    <div key={`${field.name}-expanded`} className="relative animate-fadeIn">
      <div className="flex items-center space-x-2 mb-3">
        <Icon className={`w-4 h-4 ${color.textLight}`} />
        <span className="text-xs font-medium text-muted">{field.displayName}</span>
        <button
          type="button"
          onClick={onCollapse}
          className="ml-auto p-1 text-disabled hover:text-body rounded hover:bg-surface-active"
          title="Close"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="bg-surface border border-edge rounded-lg p-4 space-y-3">
        {teams.length > 0 ? (
          <>
            <p className="text-xs text-muted mb-2">
              Drag and drop teams to reorder them. The position number will update automatically.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedTeams}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {orderedTeams.map((teamName, idx) => {
                    const position = teamMetadata?.[teamName]?.[field.name];
                    const displayPosition = position !== undefined && position !== null 
                      ? Number(position) 
                      : idx + 1;
                    return (
                      <SortableTeamItem
                        key={teamName}
                        teamName={teamName}
                        position={displayPosition}
                        color={color}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
            <div className="pt-2 border-t border-edge flex gap-2">
              <button
                type="button"
                onClick={handleClear}
                className="flex-1 px-4 py-2 border border-edge-input text-body text-sm font-medium rounded-md transition-colors hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                data-testid={`${field.name}-clear-button`}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onMetadataSave) {
                    // Always build fresh metadata from current orderedTeams state
                    // Start with a copy of current metadata to preserve other fields
                    const updated: Record<string, Record<string, any>> = {};
                    
                    // Copy existing metadata for all teams (preserving other fields)
                    teams.forEach(teamName => {
                      if (latestMetadataRef.current[teamName]) {
                        updated[teamName] = { ...latestMetadataRef.current[teamName] };
                      } else {
                        updated[teamName] = {};
                      }
                    });
                    
                    // Check if orderedTeams is in default order
                    const isDefaultOrder = JSON.stringify([...teams].sort()) === JSON.stringify([...orderedTeams].sort()) &&
                                           teams.every(teamName => orderedTeams.includes(teamName)) &&
                                           orderedTeams.length === teams.length;
                    
                    // If cleared and still in default order, remove positions
                    // Otherwise, build positions from current orderedTeams state
                    if (isClearedRef.current && isDefaultOrder) {
                      // Remove positions for all teams
                      teams.forEach(teamName => {
                        if (updated[teamName][field.name] !== undefined) {
                          delete updated[teamName][field.name];
                          if (Object.keys(updated[teamName]).length === 0) {
                            delete updated[teamName];
                          }
                        }
                      });
                    } else {
                      // Build positions from current orderedTeams state (1-based)
                      // Use the current orderedTeams state which reflects the visual order
                      orderedTeams.forEach((teamName, idx) => {
                        if (!updated[teamName]) {
                          updated[teamName] = {};
                        }
                        // Set position based on current visual order (1-based)
                        updated[teamName][field.name] = idx + 1;
                      });
                      isClearedRef.current = false; // Reset cleared flag since we're setting positions
                    }
                    
                    // Update the ref to match what we're saving
                    latestMetadataRef.current = updated;
                    
                    // Reset manual order flag since we're saving
                    hasManualOrderRef.current = false;
                    
                    // Save the metadata
                    onMetadataSave(updated);
                  }
                  onCollapse();
                }}
                className={`flex-1 px-4 py-2 ${color.btnBg} ${color.btnHover} text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${color.ring}`}
                data-testid={`${field.name}-confirm-button`}
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted">No teams selected yet</div>
        )}
      </div>
    </div>
  );
};

const PlayMetadataSection: React.FC<PlayMetadataSectionProps> = ({
  playDate,
  comment,
  durationMinutes,
  onPlayDateChange,
  onCommentChange,
  onDurationChange,
  teams = [],
  teamMetadata = {},
  onTeamMetadataChange,
  onMetadataSave,
  readOnly = false,
  hideDateButton = false,
  playerMetadataFields = [],
  metadataOptions = {},
}) => {
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [, setWasManuallyCollapsed] = useState(false);
  const [isDurationExpanded, setIsDurationExpanded] = useState(false);
  const [, setWasDurationManuallyCollapsed] = useState(false);
  const [isDatePickerExpanded, setIsDatePickerExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    playDate ? new Date(playDate) : undefined
  );
  const [selectedTime, setSelectedTime] = useState<{ hour: number; minute: number } | null>(
    playDate ? {
      hour: new Date(playDate).getHours(),
      minute: new Date(playDate).getMinutes(),
    } : null
  );
  const commentRef = useRef<HTMLTextAreaElement>(null);
  
  // Ref to track latest metadata for saving (similar to order field)
  const latestMetadataRef = useRef<Record<string, Record<string, any>>>(teamMetadata || {});

  // State for dynamic field expansion tracking
  const [dynamicFieldStates, setDynamicFieldStates] = useState<{
    [fieldName: string]: { isExpanded: boolean; wasManuallyCollapsed: boolean; }
  }>({});

  // State for autocomplete dropdown
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});

  // Sync selectedDate with playDate prop
  useEffect(() => {
    if (playDate) {
      const date = new Date(playDate);
      setSelectedDate(date);
      setSelectedTime({
        hour: date.getHours(),
        minute: date.getMinutes(),
      });
    } else {
      setSelectedDate(undefined);
      setSelectedTime(null);
    }
  }, [playDate]);



  // Focus textarea when comment expands
  useEffect(() => {
    if (isCommentExpanded && commentRef.current) {
      commentRef.current.focus();
    }
  }, [isCommentExpanded]);

  // Update ref when teamMetadata prop changes, but only merge new values to avoid overwriting local changes
  useEffect(() => {
    if (teamMetadata) {
      // Merge prop changes into ref instead of overwriting, to preserve local unsaved changes
      const merged = { ...latestMetadataRef.current };
      Object.entries(teamMetadata).forEach(([teamName, fields]) => {
        if (!merged[teamName]) {
          merged[teamName] = {};
        }
        merged[teamName] = { ...merged[teamName], ...fields };
      });
      latestMetadataRef.current = merged;
    }
  }, [teamMetadata]);

  // Initialize all metadata fields as collapsed
  useEffect(() => {
    if (!readOnly && onTeamMetadataChange) {
      const allFields = getAllMetadataFields(playerMetadataFields);
      setDynamicFieldStates(prev => {
        let changed = false;
        const newState = { ...prev };
        allFields.forEach(field => {
          // Only initialize if not already in state - always start collapsed
          if (newState[field.name] === undefined) {
            newState[field.name] = {
              isExpanded: false,
              wasManuallyCollapsed: false,
            };
            changed = true;
          }
        });
        return changed ? newState : prev;
      });
    }
  }, [playerMetadataFields, teams, teamMetadata, readOnly, onTeamMetadataChange]);

  // Unified field helper functions
  const handleExpandField = (fieldName: string) => {
    setDynamicFieldStates(prev => {
      // Collapse all other fields and expand the selected one
      const newState: typeof prev = {};
      Object.keys(prev).forEach(key => {
        if (key === fieldName) {
          newState[key] = { isExpanded: true, wasManuallyCollapsed: false };
        } else {
          // Preserve wasManuallyCollapsed state but collapse
          newState[key] = {
            ...prev[key],
            isExpanded: false
          };
        }
      });
      // Ensure the selected field is in the state
      if (!newState[fieldName]) {
        newState[fieldName] = { isExpanded: true, wasManuallyCollapsed: false };
      }
      return newState;
    });
  };

  const handleCollapseField = (fieldName: string) => {
    setDynamicFieldStates(prev => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], isExpanded: false, wasManuallyCollapsed: true }
    }));
    // Call onMetadataSave when field is collapsed (Done is clicked)
    // Use latestMetadataRef to ensure we have the most up-to-date metadata
    if (onMetadataSave) {
      // Start with a deep copy of the current teamMetadata prop to preserve ALL existing metadata
      // This ensures we don't lose any metadata that exists in currentState but not in our local ref
      const updated: Record<string, Record<string, any>> = {};
      
      // First, copy all existing metadata from the prop (which comes from currentState)
      // This preserves metadata for all teams and all fields
      Object.entries(teamMetadata || {}).forEach(([teamName, fields]) => {
        updated[teamName] = { ...fields };
      });
      
      // Then, update with the latest changes from our ref for teams that are in the teams array
      // This ensures we have the most up-to-date values for the current teams
      teams.forEach(teamName => {
        if (latestMetadataRef.current[teamName]) {
          // Merge with existing metadata for this team, or create new entry
          if (updated[teamName]) {
            updated[teamName] = { ...updated[teamName], ...latestMetadataRef.current[teamName] };
          } else {
            updated[teamName] = { ...latestMetadataRef.current[teamName] };
          }
        } else if (!updated[teamName]) {
          // Ensure all teams in the teams array have an entry (even if empty)
          updated[teamName] = {};
        }
      });
      
      // Save the metadata
      onMetadataSave(updated);
    }
  };

  const fieldHasValue = (fieldName: string) => {
    return teams.some(teamName => {
      const value = teamMetadata?.[teamName]?.[fieldName];
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  };

  const handleDynamicFieldChange = (teamName: string, fieldName: string, value: any, fieldType?: MetadataFieldDefinition['type']) => {
    if (!onTeamMetadataChange) return;

    // Get field definition to validate type
    const allFields = getAllMetadataFields(playerMetadataFields);
    const field = allFields.find(f => f.name === fieldName);

    // Use latest metadata from ref to ensure we have the most up-to-date values
    const updated = { ...latestMetadataRef.current };
    if (!updated[teamName]) {
      updated[teamName] = {};
    }

    // Type validation and conversion
    let processedValue: any = value;
    if (value !== null && value !== '' && value !== undefined) {
      const type = fieldType || field?.type;
      if (type === 'number') {
        processedValue = typeof value === 'number' ? value : Number(value);
        if (isNaN(processedValue)) {
          return; // Invalid number, don't update
        }
      } else if (type === 'text') {
        processedValue = String(value).trim();
        if (processedValue === '') {
          processedValue = null; // Empty strings become null
        }
      } else if (type === 'boolean') {
        processedValue = Boolean(value);
      }
    }

    if (processedValue === null || processedValue === '' || processedValue === undefined) {
      delete updated[teamName][fieldName];
      if (Object.keys(updated[teamName]).length === 0) {
        delete updated[teamName];
      }
    } else {
      updated[teamName][fieldName] = processedValue;
    }

    // Update ref with latest changes
    latestMetadataRef.current = updated;
    onTeamMetadataChange(updated);
  };

  // Get filtered options for autocomplete
  const getFilteredOptions = (fieldName: string, inputKey: string): string[] => {
    const field = playerMetadataFields.find(f => f.name === fieldName);
    const predefinedOptions = field?.options || [];
    const apiOptions = metadataOptions[fieldName] || [];
    const allOptions = [...new Set([...predefinedOptions, ...apiOptions])];

    const input = inputValues[inputKey] || '';
    if (!input) return allOptions;
    return allOptions.filter(opt =>
      opt.toLowerCase().includes(input.toLowerCase())
    );
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      setSelectedDate(undefined);
      setSelectedTime(null);
      onPlayDateChange('');
      return;
    }

    setSelectedDate(date);
    
    // If time is already set, combine with new date
    if (selectedTime) {
      const dateWithTime = setMinutes(setHours(date, selectedTime.hour), selectedTime.minute);
      onPlayDateChange(dateWithTime.toISOString());
    } else {
      // Default to current time if no time selected
      const now = new Date();
      const dateWithTime = setMinutes(setHours(date, now.getHours()), now.getMinutes());
      setSelectedTime({ hour: now.getHours(), minute: now.getMinutes() });
      onPlayDateChange(dateWithTime.toISOString());
    }
  };

  const handleTimeChange = (hour: number, minute: number) => {
    setSelectedTime({ hour, minute });
    
    if (selectedDate) {
      const dateWithTime = setMinutes(setHours(selectedDate, hour), minute);
      onPlayDateChange(dateWithTime.toISOString());
    }
  };

  const handleClearDate = () => {
    setSelectedDate(undefined);
    setSelectedTime(null);
    onPlayDateChange('');
  };

  const handleCollapseComment = () => {
    setIsCommentExpanded(false);
    setWasManuallyCollapsed(true);
    if (!comment.trim()) {
      onCommentChange('');
    }
  };

  const handleExpandComment = () => {
    setIsCommentExpanded(true);
    setWasManuallyCollapsed(false);
  };

  const handleCollapseDuration = () => {
    setIsDurationExpanded(false);
    setWasDurationManuallyCollapsed(true);
  };

  const handleExpandDuration = () => {
    setIsDurationExpanded(true);
    setWasDurationManuallyCollapsed(false);
  };


  // Convert minutes to hours and minutes for display
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return format(date, 'h:mm aa');
    }
    return format(date, 'MMM d');
  };


  // Render dynamic field input based on type
  const renderDynamicFieldInput = (
    teamName: string,
    field: MetadataFieldDefinition,
    color: typeof DYNAMIC_FIELD_COLORS[number]
  ) => {
    // For number fields, use the latest value from ref to ensure we show the most up-to-date value
    // For other fields, use teamMetadata prop
    const currentValue = field.type === 'number' 
      ? (latestMetadataRef.current?.[teamName]?.[field.name] ?? teamMetadata?.[teamName]?.[field.name])
      : teamMetadata?.[teamName]?.[field.name];
    const fieldKey = `${teamName}-${field.name}`;
    const inputValue = inputValues[fieldKey] || '';
    const isDropdownOpen = dropdownOpen[fieldKey] || false;

    switch (field.type) {
      case 'number':
        return (
          <div key={teamName}>
            <label className="block text-xs font-medium text-body mb-1">
              {teamName}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ''}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Allow empty string for clearing
                if (inputValue === '') {
                  handleDynamicFieldChange(teamName, field.name, null, 'number');
                  return;
                }
                // Only allow numbers (including decimals and negative numbers)
                const numericRegex = /^-?\d*\.?\d*$/;
                if (numericRegex.test(inputValue)) {
                  const numValue = Number(inputValue);
                  // Only update if it's a valid number (not NaN)
                  if (!isNaN(numValue)) {
                    handleDynamicFieldChange(teamName, field.name, numValue, 'number');
                  }
                }
              }}
              onKeyDown={(e) => {
                const input = e.currentTarget;
                const currentValue = input.value;
                const cursorPosition = input.selectionStart || 0;
                
                // Allow: backspace, delete, tab, escape, enter
                if (
                  [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                  (e.keyCode === 65 && e.ctrlKey === true) ||
                  (e.keyCode === 67 && e.ctrlKey === true) ||
                  (e.keyCode === 86 && e.ctrlKey === true) ||
                  (e.keyCode === 88 && e.ctrlKey === true) ||
                  // Allow: home, end, left, right, down, up
                  (e.keyCode >= 35 && e.keyCode <= 40)
                ) {
                  return;
                }
                
                // Allow decimal point (.) if not already present
                if ((e.keyCode === 110 || e.keyCode === 190) && currentValue.indexOf('.') === -1) {
                  return;
                }
                
                // Allow minus sign (-) only at the start
                if (e.keyCode === 189 || e.keyCode === 109) {
                  if (cursorPosition === 0 && currentValue.indexOf('-') === -1) {
                    return;
                  }
                  e.preventDefault();
                  return;
                }
                
                // Allow numbers (0-9) on both regular and numpad
                if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
                  return;
                }
                
                // Block all other keys
                e.preventDefault();
              }}
              className={`w-full px-3 py-2 border border-edge-input rounded-md text-sm ${color.ring} focus:border-${color.name}-500`}
              placeholder={`Enter ${field.displayName.toLowerCase()}`}
              data-testid={`${field.name}-input-${teamName}`}
            />
          </div>
        );

      case 'boolean':
        return (
          <div key={teamName} className="flex items-center justify-between py-2">
            <label className="text-xs font-medium text-body">
              {teamName}
            </label>
            <input
              type="checkbox"
              checked={currentValue === true}
              onChange={(e) => {
                handleDynamicFieldChange(teamName, field.name, e.target.checked || null, 'boolean');
              }}
              className={`h-4 w-4 ${color.text} ${color.ring} border-edge-input rounded`}
              data-testid={`${field.name}-input-${teamName}`}
            />
          </div>
        );

      case 'select': {
        const selectOptions = field.options || [];
        return (
          <div key={teamName}>
            <label className="block text-xs font-medium text-body mb-1">
              {teamName}
            </label>
            <select
              value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ''}
              onChange={(e) => {
                const value = e.target.value || null;
                handleDynamicFieldChange(teamName, field.name, value, 'select');
              }}
              className={`w-full px-3 py-2 border border-edge-input rounded-md text-sm ${color.ring} bg-surface`}
              data-testid={`${field.name}-input-${teamName}`}
            >
              <option value="">Select {field.displayName.toLowerCase()}...</option>
              {selectOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      }

      case 'multiselect': {
        const multiselectOptions = field.options || [];
        const selectedValues: string[] = Array.isArray(currentValue) ? currentValue : [];
        return (
          <div key={teamName}>
            <label className="block text-xs font-medium text-body mb-1">
              {teamName}
            </label>
            <div className="space-y-1 border border-edge-input rounded-md p-2 bg-surface">
              {multiselectOptions.length > 0 ? (
                multiselectOptions.map((option) => (
                  <label key={option} className="flex items-center space-x-2 cursor-pointer hover:bg-surface-hover p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => {
                        let newValues: string[];
                        if (e.target.checked) {
                          newValues = [...selectedValues, option];
                        } else {
                          newValues = selectedValues.filter(v => v !== option);
                        }
                        handleDynamicFieldChange(teamName, field.name, newValues.length > 0 ? newValues : null, 'multiselect');
                      }}
                      className={`h-4 w-4 ${color.text} ${color.ring} border-edge-input rounded`}
                      data-testid={`${field.name}-input-${teamName}-${option}`}
                    />
                    <span className="text-sm text-body">{option}</span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-muted">No options defined</div>
              )}
            </div>
          </div>
        );
      }

      case 'order': {
        // Order type is handled in the expanded panel with drag-and-drop
        // This case should not be reached in renderDynamicFieldInput
        return null;
      }

      case 'text':
      default: {
        const allOptions = getFilteredOptions(field.name, fieldKey);
        const hasOptions = allOptions.length > 0;

        return (
          <div key={teamName} className="relative">
            <label className="block text-xs font-medium text-body mb-1">
              {teamName}
            </label>
            <input
              type="text"
              value={currentValue !== undefined ? String(currentValue) : inputValue}
              onChange={(e) => {
                const value = e.target.value;
                setInputValues(prev => ({ ...prev, [fieldKey]: value }));
                handleDynamicFieldChange(teamName, field.name, value || null, 'text');
              }}
              onFocus={() => setDropdownOpen(prev => ({ ...prev, [fieldKey]: true }))}
              onBlur={() => {
                setTimeout(() => setDropdownOpen(prev => ({ ...prev, [fieldKey]: false })), 200);
              }}
              className={`w-full px-3 py-2 border border-edge-input rounded-md text-sm ${color.ring}`}
              placeholder={`Enter ${field.displayName.toLowerCase()}`}
              data-testid={`${field.name}-input-${teamName}`}
            />

            {/* Autocomplete dropdown */}
            {hasOptions && isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-surface border border-edge-input rounded-md shadow-lg max-h-40 overflow-y-auto">
                {allOptions.length > 0 ? (
                  <ul>
                    {allOptions.map((option) => (
                      <li key={option}>
                        <button
                          data-track="metadata_option"
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-surface-hover focus:bg-surface-hover focus:outline-none text-sm"
                          onClick={() => {
                            handleDynamicFieldChange(teamName, field.name, option, 'text');
                            setInputValues(prev => ({ ...prev, [fieldKey]: '' }));
                            setDropdownOpen(prev => ({ ...prev, [fieldKey]: false }));
                          }}
                        >
                          {option}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : inputValue && (
                  <div className="px-3 py-2 text-sm text-muted">
                    No suggestions found
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }
    }
  };

  // Render dynamic field button
  const renderDynamicFieldButton = (field: MetadataFieldDefinition, index: number) => {
    const fieldState = dynamicFieldStates[field.name];
    const isExpanded = fieldState?.isExpanded || false;
    const hasValue = fieldHasValue(field.name);
    const Icon = getIconComponent(field.icon, field.type);
    const color = getDynamicFieldColor(index);

    if (isExpanded) return null;

    return (
      <div key={field.name} className="flex flex-col items-center">
        <button
          data-track="metadata_field_open"
          type="button"
          onClick={() => handleExpandField(field.name)}
          data-testid={`${field.name}-button`}
          className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
            hasValue
              ? `${color.border} ${color.bg} ${color.bgHover}`
              : `border-dashed border-edge-input hover:${color.border} hover:${color.bg}`
          }`}
          title={field.displayName}
        >
          <Icon className={`w-5 h-5 ${hasValue ? color.text : 'text-disabled'}`} />
        </button>
        <span className="text-xs text-muted mt-1.5 text-center leading-tight max-w-[60px] whitespace-pre-line">
          {field.displayName.replace(/ /g, '\n')}
        </span>
      </div>
    );
  };

  // Render dynamic field expanded panel
  const renderDynamicFieldExpanded = (field: MetadataFieldDefinition, index: number) => {
    const fieldState = dynamicFieldStates[field.name];
    const isExpanded = fieldState?.isExpanded || false;
    const Icon = getIconComponent(field.icon, field.type);
    const color = getDynamicFieldColor(index);

    if (!isExpanded || !onTeamMetadataChange) return null;

    // Special handling for order type with drag-and-drop
    if (field.type === 'order') {
      return (
        <OrderFieldExpanded
          key={`${field.name}-expanded`}
          field={field}
          index={index}
          color={color}
          Icon={Icon}
          teams={teams}
          teamMetadata={teamMetadata}
          onTeamMetadataChange={onTeamMetadataChange}
          onMetadataSave={onMetadataSave}
          onCollapse={() => handleCollapseField(field.name)}
        />
      );
    }

    return (
      <div key={`${field.name}-expanded`} className="relative animate-fadeIn">
        <div className="flex items-center space-x-2 mb-3">
          <Icon className={`w-4 h-4 ${color.textLight}`} />
          <span className="text-xs font-medium text-muted">{field.displayName}</span>
          <button
            type="button"
            onClick={() => handleCollapseField(field.name)}
            className="ml-auto p-1 text-disabled hover:text-body rounded hover:bg-surface-active"
            title="Close"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-surface border border-edge rounded-lg p-4 space-y-3">
          {teams.length > 0 ? (
            <>
              {teams.map(teamName => renderDynamicFieldInput(teamName, field, color))}
              <div className="pt-2 border-t border-edge">
                <button
                  data-track="metadata_field_done"
                  type="button"
                  onClick={() => handleCollapseField(field.name)}
                  className={`w-full px-4 py-2 ${color.btnBg} ${color.btnHover} text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${color.ring}`}
                  data-testid={`${field.name}-confirm-button`}
                >
                  Done
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm text-muted">No teams selected yet</div>
          )}
        </div>
      </div>
    );
  };


  // Read-only mode
  if (readOnly) {
    return (
      <div className="flex flex-wrap items-start gap-3">
        {playDate && (
          <div className="flex items-center space-x-2 text-sm text-body">
            <CalendarIcon className="w-4 h-4 text-primary-500" />
            <span>{new Date(playDate).toLocaleString()}</span>
          </div>
        )}
        {durationMinutes && (
          <div className="flex items-center space-x-2 text-sm text-body">
            <ClockIcon className="w-4 h-4 text-purple-500" />
            <span>{formatDuration(durationMinutes)}</span>
          </div>
        )}
        {/* All metadata fields (system + game-defined) in read-only mode */}
        {getAllMetadataFields(playerMetadataFields).map((field, index) => {
          const hasValue = fieldHasValue(field.name);
          if (!hasValue) return null;

          const Icon = getIconComponent(field.icon, field.type);
          const color = getDynamicFieldColor(index);

          return (
            <div key={field.name} className="flex items-center space-x-2 text-sm text-body">
              <Icon className={`w-4 h-4 ${color.textLight}`} />
              <span>{field.displayName} set</span>
            </div>
          );
        })}
        {comment && (
          <div className="w-full mt-2 p-3 bg-surface-base rounded-lg text-sm text-body">
            {comment}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="play-metadata-section">
      {/* Compact buttons row */}
      <div className="flex flex-wrap items-start justify-center gap-3">
        {/* Date picker button (only show if date picker not expanded and not hidden) */}
        {!hideDateButton && !isDatePickerExpanded && (
          <div className="flex flex-col items-center">
            <button
              data-track="play_date_open"
              type="button"
              onClick={() => setIsDatePickerExpanded(true)}
              data-testid="date-picker-button"
              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                playDate
                  ? 'border-primary-400 bg-primary-50 hover:bg-primary-100'
                  : 'border-dashed border-edge-input hover:border-primary-400 hover:bg-primary-50'
              }`}
              title={playDate ? formatDate(playDate) : 'Set date & time'}
            >
              <CalendarIcon className={`w-5 h-5 ${playDate ? 'text-primary-600' : 'text-disabled'}`} />
            </button>
            {playDate ? (
              <div className="flex items-center space-x-1 mt-1.5" data-testid="selected-date-label">
                <span className="text-xs text-body">{formatDate(playDate)}</span>
                <button
                  type="button"
                  onClick={handleClearDate}
                  data-testid="clear-date-button"
                  className="p-0.5 text-disabled hover:text-body rounded-full hover:bg-surface-active"
                  title="Clear date"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <span className="text-xs text-muted mt-1.5 text-center leading-tight">
                Date &<br />Time
              </span>
            )}
          </div>
        )}

        {/* Notes button (only show if comment not expanded) */}
        {!isCommentExpanded && (
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={handleExpandComment}
              data-testid="add-notes-button"
              className="w-10 h-10 rounded-full border-2 border-dashed border-edge-input hover:border-green-400 hover:bg-green-50 flex items-center justify-center transition-all"
              title="Add notes"
            >
              <ChatBubbleLeftIcon className="w-5 h-5 text-disabled" />
            </button>
            <span className="text-xs text-muted mt-1.5 text-center leading-tight">
              Add<br />Notes
            </span>
          </div>
        )}

        {/* Duration button (only show if duration not expanded and onDurationChange is provided) */}
        {onDurationChange && !isDurationExpanded && (
          <div className="flex flex-col items-center">
            <button
              data-track="play_duration_open"
              type="button"
              onClick={handleExpandDuration}
              data-testid="add-duration-button"
              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                durationMinutes
                  ? 'border-purple-400 bg-purple-50 hover:bg-purple-100'
                  : 'border-dashed border-edge-input hover:border-purple-400 hover:bg-purple-50'
              }`}
              title={durationMinutes ? formatDuration(durationMinutes) : 'Set duration'}
            >
              <ClockIcon className={`w-5 h-5 ${durationMinutes ? 'text-purple-600' : 'text-disabled'}`} />
            </button>
            {durationMinutes ? (
              <div className="flex items-center space-x-1 mt-1.5" data-testid="selected-duration-label">
                <span className="text-xs text-body">{formatDuration(durationMinutes)}</span>
                <button
                  type="button"
                  onClick={() => onDurationChange?.(null)}
                  data-testid="clear-duration-button"
                  className="p-0.5 text-disabled hover:text-body rounded-full hover:bg-surface-active"
                  title="Clear duration"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <span className="text-xs text-muted mt-1.5 text-center leading-tight">
                Duration<br />&nbsp;
              </span>
            )}
          </div>
        )}

        {/* All metadata field buttons (system + game-defined) */}
        {onTeamMetadataChange && getAllMetadataFields(playerMetadataFields).map((field, index) =>
          renderDynamicFieldButton(field, index)
        )}
      </div>

      {/* Expanded date picker field */}
      {isDatePickerExpanded && (
        <div className="relative animate-fadeIn">
          <div className="flex items-center space-x-2 mb-1.5">
            <CalendarIcon className="w-4 h-4 text-primary-500" />
            <span className="text-xs font-medium text-muted">Date & Time</span>
            <button
              type="button"
              onClick={() => setIsDatePickerExpanded(false)}
              className="ml-auto p-1 text-disabled hover:text-body rounded hover:bg-surface-active"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-4 bg-surface border border-edge-input rounded-lg p-4">
            {/* Calendar */}
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="rounded-md"
              classNames={{
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                month: 'space-y-4',
                caption: 'flex justify-center pt-1 relative items-center',
                caption_label: 'text-sm font-medium text-heading',
                nav: 'space-x-1 flex items-center',
                nav_button: 'h-7 w-7 bg-transparent p-0 rounded-md hover:bg-surface-active',
                nav_button_previous: 'absolute left-1',
                nav_button_next: 'absolute right-1',
                table: 'w-full border-collapse space-y-1',
                head_row: 'flex',
                head_cell: 'text-muted rounded-md w-9 font-normal text-[0.8rem]',
                row: 'flex w-full mt-2',
                cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-primary-50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-surface-active',
                day_selected: 'bg-primary-600 text-white hover:bg-primary-600 hover:text-white focus:bg-primary-600 focus:text-white',
                day_today: 'bg-primary-100 text-primary-900 font-semibold',
                day_outside: 'text-disabled opacity-50',
                day_disabled: 'text-faint opacity-50',
                day_range_middle: 'aria-selected:bg-primary-50 aria-selected:text-heading',
                day_hidden: 'invisible',
              }}
            />

            {/* Time picker */}
            {selectedDate && (
              <div className="border-t border-edge pt-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="w-4 h-4 text-muted" />
                  <span className="text-sm font-medium text-body">Time</span>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedTime?.hour ?? 12}
                    onChange={(e) => handleTimeChange(Number(e.target.value), selectedTime?.minute ?? 0)}
                    className="px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span className="text-muted">:</span>
                  <select
                    value={selectedTime?.minute ?? 0}
                    onChange={(e) => handleTimeChange(selectedTime?.hour ?? 12, Number(e.target.value))}
                    className="px-3 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {[0, 15, 30, 45].map((min) => (
                      <option key={min} value={min}>
                        {String(min).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-edge mt-4">
              <button
                type="button"
                onClick={() => setIsDatePickerExpanded(false)}
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                data-testid="confirm-date-button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded comment field */}
      {isCommentExpanded && (
        <div className="relative animate-fadeIn">
          <div className="flex items-center space-x-2 mb-1.5">
            <ChatBubbleLeftIcon className="w-4 h-4 text-green-500" />
            <span className="text-xs font-medium text-muted">Notes</span>
            <button
              type="button"
              onClick={handleCollapseComment}
              className="ml-auto p-1 text-disabled hover:text-body rounded hover:bg-surface-active"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-surface border border-edge rounded-lg p-4">
            <textarea
              ref={commentRef}
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              rows={3}
              data-testid="notes-textarea"
              className="w-full border border-edge-input rounded-lg px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500 resize-none"
              placeholder="Any notes about this play..."
            />
            <div className="pt-3 border-t border-edge mt-3">
              <button
                type="button"
                onClick={handleCollapseComment}
                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                data-testid="confirm-notes-button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded duration field */}
      {isDurationExpanded && onDurationChange && (
        <div className="relative animate-fadeIn" data-testid="duration-expanded">
          <div className="flex items-center space-x-2 mb-3">
            <ClockIcon className="w-4 h-4 text-purple-500" />
            <span className="text-xs font-medium text-muted">Duration</span>
            <button
              type="button"
              onClick={handleCollapseDuration}
              className="ml-auto p-1 text-disabled hover:text-body rounded hover:bg-surface-active"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-surface border border-edge rounded-lg p-4">
            <DurationDialPicker
              durationMinutes={durationMinutes || 0}
              onChange={(minutes) => onDurationChange(minutes > 0 ? minutes : null)}
            />
            <div className="pt-3 border-t border-edge mt-3">
              <button
                type="button"
                onClick={handleCollapseDuration}
                className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                data-testid="confirm-duration-button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All metadata field expanded panels (system + game-defined) */}
      {getAllMetadataFields(playerMetadataFields).map((field, index) =>
        renderDynamicFieldExpanded(field, index)
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default PlayMetadataSection;
